// ─── Enums ───────────────────────────────────────────────────────────────────

export enum DistributionTypeEnum {
  GRID = 'GRID',
  PERCENTAGE = 'PERCENTAGE',
  PERCENTAGE_GRID = 'PERCENTAGE_GRID',
  FIXED_PLUS_BUSINESS_ENTITY_WEIGHT_AVG = 'FIXED_PLUS_BUSINESS_ENTITY_WEIGHT_AVG',
  FIXED = 'FIXED',
  LIMITED_FREE_GRID = 'GRID_WITH_NB_TR_FREE',
}

export enum InternalFeeOperationTypeEnum {
  PAYMENT = 'payment',
  INTERNAL_TRANSFER = 'internal_transfer',
  EXTERNAL_TRANSFER = 'external_transfer',
  ADD_WITHDRAW_FUNDS = 'add_withdraw_funds',
}

export enum InternalRevenueOperationTypeEnum {
  CASHIN = 'cashin',
  CASHOUT = 'cashout',
  VEND = 'vend',
}

export enum OperationGroupEnum {
  INTERNAL_FEE = 'internal_fee',
  EXTERNAL_FEE = 'external_fee',
  INTERNAL_REVENUE = 'internal_revenue',
  EXTERNAL_REVENUE = 'external_revenue',
}

export enum OperationActionEnum {
  EDIT = 'edit',
  VIEW = 'view',
}

export enum StrategyTypeEnum {
  GLOBAL = 'global',
  BE = 'be',
}

export enum PeriodTypeEnum {
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  YEAR = 'YEAR',
}

export enum OperationTypeEnum {
  FEE = 'FEE',
  REVENUE = 'REVENUE',
}

export enum PricingTypeEnum {
  DEFAULT = 'default',
  CUSTOM = 'custom',
}

export enum SubscriptionPlanEnum {
  STARTER = 'starter',
  STANDARD = 'standard',
  PRO = 'pro',
  BUSINESS = 'business',
}

export enum SubscriptionPlanFrequencyEnum {
  FREE = '',
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
}

// ─── Group helpers ────────────────────────────────────────────────────────────

export function isInternalGroup(group: OperationGroupEnum): boolean {
  return (
    group === OperationGroupEnum.INTERNAL_FEE ||
    group === OperationGroupEnum.INTERNAL_REVENUE
  );
}

export function isExternalGroup(group: OperationGroupEnum): boolean {
  return (
    group === OperationGroupEnum.EXTERNAL_FEE ||
    group === OperationGroupEnum.EXTERNAL_REVENUE
  );
}

export function isRevenueGroup(group: OperationGroupEnum): boolean {
  return (
    group === OperationGroupEnum.INTERNAL_REVENUE ||
    group === OperationGroupEnum.EXTERNAL_REVENUE
  );
}

// ─── Models ──────────────────────────────────────────────────────────────────

export interface GridModel {
  id?: string | number;
  min: number;
  max: number;
  fee?: number;
  revenue?: number;
}

export interface ProviderCurrency {
  allowDecimals: boolean;
  currencyLabel: string;
  id: number;
}

export interface ProviderModel {
  id: number;
  providerCode: string;
  providerCodeType: string;
  providerName: string;
  providerType: string;
}

export interface Provider {
  id: number;
  providerCode: string;
  providerCodeType: string;
  providerCurrency: ProviderCurrency;
  providerName: string;
}

export interface InternalOperationModel {
  id: number;
  operationType:
    | InternalFeeOperationTypeEnum
    | InternalRevenueOperationTypeEnum;
}

export interface ExternalOperationModel {
  operationType: OperationTypeEnum;
  nostroOperationId: number;
  microOperationType:
    | InternalFeeOperationTypeEnum
    | InternalRevenueOperationTypeEnum;
  providerId: number;
  providerName: string;
}

export interface ExternalDistributionModel {
  nostroOperationId: number;
  compensationType: DistributionTypeEnum;
  flowDirection?: string;
  margin: number;
  microOperationId:
    | InternalFeeOperationTypeEnum
    | InternalRevenueOperationTypeEnum;
  periodType?: string;
  productType?: string;
  providerNostroAccount?: string;
  validationType?: string;
  provider: ProviderModel;
  executionSystem?: string;
  serviceName?: string;
  grids: GridModel[];
}

export interface PricingPayloadModel {
  id?: number;
  distributionType: DistributionTypeEnum;
  operationType:
    | InternalFeeOperationTypeEnum
    | InternalRevenueOperationTypeEnum;
  grids?: GridModel[];
  fixed?: number | null;
  percentage?: number | null;
  periodType?: string | null;
  enabledDistributionTypes?: DistributionTypeEnum[];
  disabledDistributionTypes?: DistributionTypeEnum[];
  isDefaultPricing?: boolean;
}

export interface PricingParamsModel {
  strategy?: StrategyTypeEnum;
  action: OperationActionEnum;
  type: OperationGroupEnum;
  operation:
    | InternalFeeOperationTypeEnum
    | InternalRevenueOperationTypeEnum;
  operationObject?: ExternalOperationModel;
  id?: number;
}

/** Slimmed payload sent to the API — nulls stripped by distributionType. */
export interface PricingApiPayload {
  id?: number;
  distributionType: DistributionTypeEnum;
  operationType: string;
  grids: GridModel[];
  percentage: number | null;
  fixed: number | null;
  periodType: string | null;
}
